import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getOppositeTrait,
  getTailwindColorClasses,
  getTraitColor,
  getTraitTitle,
  getTraitFromEnglishCode,
} from "../../utils/personality-helper";
import Loader from "../../components/loader";
import Header from "../../components/header";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top";
import { isGuid } from "guid";

import {
  generateContentKey,
  PERSONALITY_TEST_ASSESSMENT_ID,
  wrapScoresWithTraitLevel,
} from "../../utils/general-config";
import {
  getBannerImage,
  getColorCode,
  getCookie,
  getImage,
  getPersonalityBannerImage,
} from "../../utils/utility-functions";
import { LOCALE, REPORT_NOT_FOUND } from "../../utils/constants";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import { useTranslation } from "react-i18next";

const PersonalityReport = ({
  viewAssessmentReportConnect,
  funnelStep,
  getAssessmentAttemptByUserIdConnect,
  getUserByIdConnect,
  getAssessmentResultByAssessmentAttemptIdConnect,
  createContentMapConnect,
  getContentLatestVersionConnect,
  sendToFriendReportOnEmailConnect,
}) => {
  const [data, setData] = useState({});
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { userGUID, gender, isAuth } = funnelStep || {};
  const [isLoading, setIsLoading] = useState(false);
  const [assessmentAttemptData, setAssessmentAttemptData] = useState({});
  const { t } = useTranslation();
  const { locale } = useParams();
  const currentLocale = locale || LOCALE.EN;
  const userId = queryParams.get("uid") || userGUID;
  const assessmentAttemptId = queryParams.get("aai");
  const [userData, setUserData] = useState(null);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  // const token = getCookie("jwtToken");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);
  useEffect(() => {
    const fetchAllData = async () => {
      if (!userId || !assessmentAttemptId || !isGuid(assessmentAttemptId))
        return;
      try {
        setIsLoading(true);

        // Fetch user data
        const user = await getUserByIdConnect(userId);
        if (user) {
          setUserData(user);
        }

        // Fetch assessment attempts
        const response = await getAssessmentAttemptByUserIdConnect(
          userId,
          currentLocale
        );
        if (response) {
          const filterByCurrentAssessmentAttemptId = response?.filter(
            (item) => item.assessmentAttemptId === assessmentAttemptId
          );
          setCurrentAssessmentAttemptData(filterByCurrentAssessmentAttemptId);

          const filterLatestPersonalityData = response
            .filter(
              (item) =>
                item.assessmentId === PERSONALITY_TEST_ASSESSMENT_ID &&
                item.personality !== null
            )
            ?.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
            ?.map((item, index) => (index === 0 ? item : null))
            ?.filter(Boolean)[0];
          setAssessmentAttemptData(filterLatestPersonalityData);
        }

        // Fetch assessment report
        let res = await viewAssessmentReportConnect(
          userId,
          assessmentAttemptId,
          currentLocale !== LOCALE.EN && currentLocale
        );

        if (res === REPORT_NOT_FOUND) {
          const result = await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
          if (result) {
            const { modelType, userTraits } = result;
            const mappedUserTrait =
              userTraits?.length > 0
                ? wrapScoresWithTraitLevel(userTraits)
                : [];
            const contentKey = generateContentKey(mappedUserTrait);

            if (modelType && contentKey) {
              const latestVersion = await getContentLatestVersionConnect({
                modelType,
                contentKey,
              });

              if (latestVersion) {
                const contentMapData = {
                  userGuid: user?.userGuid, // Use the user from this scope instead of userData
                  contentKey,
                  modelType,
                  assessmentAttemptId,
                  version: latestVersion,
                };

                await createContentMapConnect(contentMapData);
                res = await viewAssessmentReportConnect(
                  userId,
                  assessmentAttemptId
                );
              }
            }
          }
        }

        if (!!res) {
          setData(res);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [userId, assessmentAttemptId, currentLocale]);

  const userTraitPercentage = data && data?.traitsForPercentage;

  const tratiPercentage =
    userTraitPercentage &&
    userTraitPercentage?.map((item) => {
      return {
        ...item,
        leftTrait:
          item.percentage <= 50
            ? getTraitFromEnglishCode(item?.englishCode, t)
            : getOppositeTrait(item?.englishCode, t),
        rightTrait:
          item.percentage > 50
            ? getTraitFromEnglishCode(item?.englishCode, t)
            : getOppositeTrait(item?.englishCode, t),
        traitTitle: getTraitTitle(item?.englishCode, t),
        color: getTraitColor(item?.englishCode),
      };
    });

  const personalityGroup = assessmentAttemptData
    ? assessmentAttemptData?.personality?.role
    : "";
  const personalityName = assessmentAttemptData
    ? assessmentAttemptData?.personality?.name
    : "";

  const currentPersonalityGroup = currentAssessmentAttemptData
    ? currentAssessmentAttemptData[0]?.personality?.role
    : "";
  const currentPersonalityName = currentAssessmentAttemptData
    ? currentAssessmentAttemptData[0]?.nameEnglishCode
    : "";

  const bannerImage = getBannerImage(currentPersonalityGroup);
  const personalityImage = getImage(
    currentPersonalityGroup,
    currentPersonalityName,
    true,
    gender || userData?.gender
  );

  const personalityColorDark = getColorCode(
    currentPersonalityGroup && currentPersonalityGroup
  ).dark;

  const personalityColorLight = getColorCode(
    currentPersonalityGroup && currentPersonalityGroup
  ).light;

  const createSectionHeader = (title, gradientColors, imageSrc) => {
    return (
      <div
        className={`relative min-h-[12rem] sm:h-64 bg-gradient-to-r ${gradientColors} flex items-center px-4 sm:px-8`}
      >
        <img
          src={imageSrc}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-auto md:h-full object-cover object-right"
        />
        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientColors} to-transparent opacity-65`}
        ></div>

        <div className="relative z-10 max-w-[90%] px-4 sm:px-0 py-4 sm:py-0">
          <h2 className="text-4xl sm:text-4xl font-bold mb-2 shadow-text break-words text-white">
            {title}
          </h2>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div>
        <Loader isLoading={isLoading} />
      </div>
    );
  }

  const Card = ({
    englishCode,
    percentage,
    leftTrait,
    rightTrait,
    traitTitle,
    color,
  }) => {
    const textToBeUsed = `${t(
      "report.personality.you_are_text"
    )} ${getTraitFromEnglishCode(englishCode, t)}: ${percentage}%`;
    const colorClasses = getTailwindColorClasses(color);

    return (
      <div className="w-full mx-auto">
        {/* MOBILE VIEW */}
        <div className="p-4 rounded-lg bg-white shadow-md sm:hidden">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2">
            <div className="md:w-1/3">
              <h4
                className={`${colorClasses.textColor} text-sm font-semibold text-center md:text-left`}
              >
                {traitTitle}
              </h4>
            </div>

            <div className="text-center mt-1 md:mt-0 md:w-1/3">
              <span
                className={`${colorClasses.subTextColor} text-xs font-medium`}
              >
                {textToBeUsed}
              </span>
            </div>

            <div className="hidden md:block md:w-1/3"></div>
          </div>

          <div className="flex items-center w-full mt-4">
            <div className="w-20 text-right pr-2">
              <span
                className={`${colorClasses.subTextColor} text-xs font-medium`}
              >
                {leftTrait}
              </span>
            </div>

            <div className="relative flex-1">
              <div
                className={`${colorClasses.bgLightColor} w-full rounded-full h-2`}
              >
                <div
                  className={`${colorClasses.progressBarColor} absolute top-0 left-0 h-2 rounded-full`}
                  style={{ width: `${percentage}%` }}
                ></div>

                <div
                  className={`absolute top-1/2 w-4 h-4 bg-white rounded-full transform -translate-x-1/2 -translate-y-1/2 border-2 ${colorClasses.borderColor} shadow-sm`}
                  style={{ left: `${percentage}%` }}
                ></div>
              </div>
            </div>

            <div className="w-20 text-left pl-2">
              <span
                className={`${colorClasses.subTextColor} text-xs font-medium`}
              >
                {rightTrait}
              </span>
            </div>
          </div>
        </div>

        {/* DESKTOP VIEW */}
        <div className="p-4 rounded-lg bg-white shadow-md hidden sm:block">
          <div className="mb-1">
            <h4 className={`${colorClasses.textColor} text-sm font-semibold`}>
              {traitTitle}
            </h4>
          </div>
          <div className="relative pt-4">
            <div
              className="absolute text-center -top-1"
              style={{
                left: `${percentage}%`,
                transform: "translateX(-50%)",
              }}
            >
              <span
                className={`${colorClasses.subTextColor} text-xs font-medium block`}
              >
                {textToBeUsed}
              </span>
            </div>
            <div className="flex items-center w-full ">
              <div className="w-20 text-right pr-2">
                <span
                  className={`${colorClasses.subTextColor} text-xs font-medium`}
                >
                  {leftTrait}
                </span>
              </div>
              <div className="relative flex-1">
                <div
                  className={`${colorClasses.bgLightColor} w-full rounded-full h-2`}
                >
                  <div
                    className={`${colorClasses.progressBarColor} absolute top-0 left-0 h-2 rounded-full`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                  <div
                    className={`absolute top-1/2 w-4 h-4 bg-white rounded-full transform -translate-x-1/2 -translate-y-1/2 border-2 ${colorClasses.borderColor} shadow-sm`}
                    style={{ left: `${percentage}%` }}
                  ></div>
                </div>
              </div>
              <div className="w-20 text-left pl-2">
                <span
                  className={`${colorClasses.subTextColor} text-xs font-medium`}
                >
                  {rightTrait}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header
        personalityGroup={
          personalityGroup ? personalityGroup.toLowerCase() : ""
        }
      />
      <BackToTopButton />
      <div className="flex justify-center items-center">
        <div className="container mx-auto p-4 sm:p-8">
          {/* {----------------------------HERO SECTION------------------------------------} */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="relative h-64 sm:h-96 bg-gradient-to-r p-10">
              {/* from-purple-500 via-purple-500 to-violet-700 */}
              {/* Background image */}
              <img
                // src="https://insightfultraits.com/wp-content/uploads/2024/08/bg-estj.png"
                src={bannerImage}
                alt="Commander Personality Background"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              {/* <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-purple-500 to-transparent opacity-75"></div> */}

              <div
                className="absolute inset-0 flex items-center justify-between "
                id={reportType}
              >
                <div className="w-full lg:pl-10 pl-6 sm:w-2/5" id={reportType}>
                  <h1
                    className="text-3xl lg:text-4xl font-bold mb-2 shadow-text text-white"
                    id={reportType}
                  >
                    {data?.content?.title} {data?.content?.code}
                  </h1>
                  <h4
                    className="text-xl lg:text-3xl font-light shadow-text text-white"
                    id={reportType}
                  >
                    {data?.content?.flavor_Text}
                  </h4>
                </div>
                {/* Right-side image */}
                <div className="hidden sm:block w-max h-full aspect-square">
                  <img
                    src={personalityImage}
                    // src="https://insightfultraits.com/wp-content/uploads/2024/08/people-estj-1.png"
                    alt="Commander Personality"
                    className="w-auto h-full object-bottom float-right md:-mr-0 lg:mr-10 mb-2 rounded-full"
                  />
                </div>
              </div>
            </div>
            {isAuth && (
              <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-4">
                <ShareButton
                  currentAssessmentAttemptData={currentAssessmentAttemptData[0]}
                  userGUID={userGUID}
                  userData={userData}
                />

                <SendToFriendButton
                  currentAssessmentAttemptData={currentAssessmentAttemptData[0]}
                  userGUID={userGUID}
                  userData={userData}
                  CreateGuestUserConnect={sendToFriendReportOnEmailConnect}
                />
              </div>
            )}

            <div className="p-6 sm:p-10">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="w-full lg:w-1/4">
                  <div
                    style={{ background: personalityColorLight }}
                    className={` rounded-xl p-6 shadow-md`}
                  >
                    <h3
                      style={{ color: personalityColorDark }}
                      className={`text-2xl font-bold mb-4 `}
                    >
                      {/* Key Attributes */}
                      {t("report.personality.key_attributes")}
                    </h3>
                    <ul className="space-y-3 mb-6">
                      {data?.content?.descriptive_Words?.map((word, index) => (
                        <li key={index} className="flex items-center">
                          <svg
                            style={{ color: personalityColorDark }}
                            className={`w-6 h-6 mr-2 `}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            ></path>
                          </svg>
                          <spa>{word}</spa>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="w-full lg:w-3/4">
                  <h2
                    style={{ color: personalityColorDark }}
                    className={`text-3xl md:text-4xl font-bold mb-6  `}
                  >
                    {`${data?.content?.group} ${t("report.personality.group_influence")}`}
                  </h2>
                  <div className="space-y-6">
                    {data?.content?.intro?.map((word, index) => (
                      <p
                        key={index}
                        className="text-lg text-gray-700 leading-relaxed"
                      >
                        {word}
                      </p>
                    ))}
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 mt-5 shadow-md">
                    <h3 className="text-xl font-bold mb-3 text-gray-800">
                      {t("report.personality.personality_scales")}
                    </h3>
                    <div className="space-y-6">
                      {tratiPercentage?.map((item) => {
                        const colorClass = getTailwindColorClasses(item?.color);
                        return (
                          <Card {...item} />
                          // <div
                          //   className="p-4 rounded-lg bg-white shadow-sm"
                          //   key={item?.trait}
                          // >
                          //   {/* Trait title */}
                          //   <div className="mb-2">
                          //     <h4
                          //       className={`${colorClass.textColor} text-sm font-semibold`}
                          //     >
                          //       {item?.traitTitle}
                          //     </h4>
                          //   </div>

                          //   {/* Centered "You are" text */}
                          //   <div className="text-center">
                          //     <span
                          //       className={`${colorClass.subTextColor} text-xs font-medium block`}
                          //     >
                          //       You are {item?.trait}:
                          //     </span>
                          //     <span
                          //       className={`${colorClass.subTextColor} text-xs font-medium block`}
                          //     >
                          //       {item?.percentage}%
                          //     </span>
                          //   </div>

                          //   {/* Trait bar with fixed-width labels */}
                          //   <div className="flex items-center w-full">
                          //     {/* Left label with fixed width */}
                          //     <div className="w-20 text-right pr-2">
                          //       <span
                          //         className={`${colorClass.subTextColor} text-xs font-medium`}
                          //       >
                          //         {item?.leftTrait}
                          //       </span>
                          //     </div>

                          //     {/* Progress bar container with fixed width */}
                          //     <div className="relative flex-1">
                          //       <div
                          //         className={`${colorClass.bgLightColor} w-full rounded-full h-2`}
                          //       >
                          //         {/* Progress Bar Fill */}
                          //         <div
                          //           className={`${colorClass.progressBarColor} absolute top-0 left-0 h-2 rounded-full`}
                          //           style={{ width: `${item?.percentage}%` }}
                          //         ></div>

                          //         {/* Circular Marker */}
                          //         <div
                          //           className={`absolute top-1/2 w-4 h-4 bg-white rounded-full transform -translate-x-1/2 -translate-y-1/2 border-2 ${colorClass.borderColor} shadow-sm`}
                          //           style={{ left: `${item?.percentage}%` }}
                          //         ></div>
                          //       </div>
                          //     </div>

                          //     {/* Right label with fixed width */}
                          //     <div className="w-20 text-left pl-2">
                          //       <span
                          //         className={`${colorClass.subTextColor} text-xs font-medium`}
                          //       >
                          //         {item?.rightTrait}
                          //       </span>
                          //     </div>
                          //   </div>
                          // </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* {------------------------------NOTABLE  Section -----------------------------------------} */}

          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            {createSectionHeader(
              data?.content?.notableCharacteristics?.rootHeading,
              "from-blue-500 via-blue-600 to-indigo-700",
              getPersonalityBannerImage("characteristics"),
            )}
            <div className="p-6 sm:p-10">
              {data?.content?.notableCharacteristics?.content?.map(
                (item, index) => (
                  <div
                    key={index}
                    className="mb-6 bg-blue-50 rounded-lg p-6 shadow-md"
                  >
                    <h3 className="text-2xl font-bold mb-4 text-blue-800 break-words overflow-hidden">
                      {item.heading}
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed break-words whitespace-pre-line">
                      {item.description}
                    </p>
                  </div>
                ),
              )}
            </div>
          </section>

          {/* {------------------------------ROLE IN GROUP Section -----------------------------------------} */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            {createSectionHeader(
              data?.content?.role_in_group?.rootHeading,
              "from-green-500 via-green-600 to-teal-700",
              getPersonalityBannerImage("role"),
            )}
            <div className="p-6 sm:p-10">
              {data?.content?.role_in_group?.content.map((item, index) => (
                <div
                  key={index}
                  className="mb-6 bg-green-50 rounded-lg p-6 shadow-md"
                >
                  <h3 className="text-2xl font-bold mb-4 text-green-800 break-words overflow-hidden">
                    {item.heading}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed break-words whitespace-pre-line">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* {------------------------------TRAIT  Section -----------------------------------------} */}
          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            {/* Create section header */}
            {createSectionHeader(
              data?.content?.traits_Personalized?.rootHeading,
              "from-purple-500 via-purple-600 to-indigo-700",
              getPersonalityBannerImage("traits"),
            )}
            <div className="p-6 sm:p-10">
              {/* Map over Traits Personalized content */}
              {data?.content?.traits_Personalized?.content?.map(
                (item, index) => (
                  <div
                    key={index}
                    className="mb-6 bg-purple-50 rounded-lg p-6 shadow-md"
                  >
                    <h3 className="text-2xl font-bold mb-4 text-purple-800 break-words overflow-hidden">
                      {item.heading}
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed break-words whitespace-pre-line">
                      {item.description}
                    </p>
                  </div>
                ),
              )}
            </div>
          </section>
          {/* {------------------------------TRAIT INTERSECTION  Section -----------------------------------------} */}
          <section class="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            {createSectionHeader(
              data?.content?.trait_Interactions_Personalized?.rootHeading,
              "from-yellow-400 via-yellow-500 to-orange-500",
              getPersonalityBannerImage("interactions"),
            )}
            <div class="p-6 sm:p-10">
              {data?.content?.trait_Interactions_Personalized?.content?.map(
                (item, index) => (
                  <div
                    key={index}
                    className="mb-6 bg-yellow-50 rounded-lg p-6 shadow-md"
                  >
                    <h3 className="text-2xl font-bold mb-4 text-yellow-800 break-words overflow-hidden">
                      {item?.heading}
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed break-words whitespace-pre-lined">
                      {item?.description}
                    </p>
                  </div>
                ),
              )}
            </div>
          </section>

          {/* {------------------------------CHALLENGES PERSONALIZED  Section -------------------------------} */}

          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            {/* Create section header */}
            {createSectionHeader(
              data?.content?.challenges_Personalized?.rootHeading,
              "from-red-500 via-red-600 to-pink-700",
              getPersonalityBannerImage("challenges"),
            )}
            <div className="p-6 sm:p-10">
              {/* Map over Challenges Personalized content */}
              {data?.content?.challenges_Personalized?.content?.map(
                (item, index) => (
                  <div
                    key={index}
                    className="mb-6 bg-yellow-50 rounded-lg p-6 shadow-md"
                  >
                    <h3 className="text-2xl font-bold mb-4 text-red-800 break-words overflow-hidden">
                      {item.heading}
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed break-words whitespace-pre-line">
                      {item.description}
                    </p>
                  </div>
                ),
              )}
            </div>
          </section>

          {/* {------------------------------WHAT NEXT  Section -------------------------------} */}

          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            {/* Create section header */}
            {createSectionHeader(
              data?.content?.whats_next.rootHeading,
              "from-cyan-500 via-cyan-600 to-blue-700",
              getPersonalityBannerImage("whats-next"),
            )}
            <div className="p-6 sm:p-10">
              {/* Map over What's Next content */}
              {data?.content?.whats_next.content.map((item, index) => (
                <div
                  key={index}
                  className="mb-6 bg-cyan-50 rounded-lg p-6 shadow-md"
                >
                  <h3 className="text-2xl font-bold mb-4 text-cyan-800 whitespace-normal overflow-hidden">
                    {item.heading}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* {------------------------------FUN FACT Section -------------------------------} */}

          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            {/* Create section header */}
            {createSectionHeader(
              data?.content?.fun_Facts.rootHeading,
              "from-pink-500 via-pink-600 to-purple-700",
              getPersonalityBannerImage("fun-facts"),
            )}
            <div className="p-6 sm:p-10">
              <ul className="list-disc pl-5 space-y-4">
                {/* Map over Fun Facts content */}
                {data?.content?.fun_Facts.content?.map((fact, index) => (
                  <li
                    key={index}
                    className="text-lg text-gray-700 leading-relaxed"
                  >
                    {fact}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* {------------------------------FAQ Section -------------------------------} */}

          <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            {/* Create section header */}
            {createSectionHeader(
              data?.content?.faq.rootHeading,
              "from-indigo-500 via-indigo-600 to-purple-700",
              getPersonalityBannerImage("faq"),
            )}
            <div className="p-6 sm:p-10">
              {/* Map over FAQ content */}
              {data?.content?.faq?.content?.map((item, index) => (
                <div
                  key={index}
                  className="mb-6 bg-indigo-50 rounded-lg p-6 shadow-md"
                >
                  <h3 className="text-2xl font-bold mb-4 text-indigo-800">
                    {item.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PersonalityReport;
